import React from 'react';
import { Modal, Form, Button, notification } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import { ModalProps } from '@/types/ModalProps';
import AccountsDocumentTypesReportForm from '../AccountsDocumentTypesReportForm';
import { ReportDocumentTypesFormValue } from '@/core/domain/reports/account/Report.types';
import { useDocumentTypeReport } from '@/core/domain/reports/account/Report.hooks';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { useDefaultFormErrorNotification } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';

function AccountsDocumentTypesReportModal({
  visible,
  handleVisible,
}: ModalProps) {
  const intl = useIntl();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();
  const report = useReportGenerate();
  const [form] = Form.useForm<ReportDocumentTypesFormValue>();

  const mutation = useDocumentTypeReport();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const { description, ...values } = await form.validateFields();
      const filterDescription = description
        ? description.filter((cur) => cur !== undefined && cur !== '')
        : [];

      const obj = {
        ...values,
        description:
          filterDescription.length === 1
            ? filterDescription.toString()
            : undefined,
        range:
          filterDescription.length > 1
            ? `[${filterDescription.toString().toUpperCase()}]`
            : undefined,
        format: FILE_FORMAT[format],
      };

      const result = await mutation.mutateAsync(obj);

      if (callback) {
        callback(result);
      }

      notification.success({
        message: intl.formatMessage({
          id: 'documentTypes.notification.generate.report.title',
        }),
        description: intl.formatMessage({
          id: 'documentTypes.notification.generate.report.description',
        }),
      });
      handleVisible();
    } catch (err) {
      sendFormErrorNotification(err);
    }
    setFileUnloading(format);
  };

  return (
    <Modal
      width={680}
      open={visible}
      onCancel={handleVisible}
      title={intl.formatMessage({ id: 'documentTypes.modal.report.title' })}
      footer={
        <SpaceForButton justifyContent="end">
          <Button
            type="primary"
            key="viewReport"
            block
            onClick={() =>
              handleGenerateReport('REPORT_VIEW', (result) =>
                report.openFile({
                  result,
                  fileName: 'Tipos de Documentos.pdf',
                  fileType: 'application/pdf',
                }),
              )
            }
            loading={fileLoading.REPORT_VIEW}
          >
            <FormattedMessage id="documentTypes.modal.report.buttons.viewReport" />
          </Button>

          <Button
            type="primary"
            key="pdf"
            block
            onClick={() =>
              handleGenerateReport('PDF', (result) =>
                report.downloadFile({
                  result,
                  fileName: 'Tipos de Documentos.pdf',
                }),
              )
            }
            loading={fileLoading.PDF}
          >
            <FormattedMessage id="documentTypes.modal.report.buttons.pdf" />
          </Button>

          <Button
            type="primary"
            key="csv"
            block
            onClick={() =>
              handleGenerateReport('CSV', (result) =>
                report.downloadFile({
                  result,
                  fileName: 'Tipos de Documentos.csv',
                }),
              )
            }
            loading={fileLoading.CSV}
          >
            <FormattedMessage id="documentTypes.modal.report.buttons.csv" />
          </Button>
        </SpaceForButton>
      }
    >
      <AccountsDocumentTypesReportForm form={form} />
    </Modal>
  );
}

export default AccountsDocumentTypesReportModal;
